import { Paper, Tabs } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useGroup } from 'hooks/api/useGroups';
import { CenteredLoading } from 'components/CenteredLoading';
import { NotFound } from 'components/NotFound/NotFound';
import { DEFAULT_ICON_SIZE } from 'utils/general';
import { BsClipboard, BsDisplay, BsFolder } from 'react-icons/bs';
import { showUsaFeature, showWorklistFeature } from 'utils/app';
import { GroupSettingsGeneral } from './GroupSettingsGeneral';
import { GroupSettingsModules } from './GroupSettingsModules';
import { GroupSettingsTabs } from './Forms/GroupSettings.helpers';
import { GroupSettingsRules } from './GroupSettingsRules';

export const GroupSettings = () => {
  const { t } = useTranslation();
  const { groupId, tab } = useParams<{ groupId: string; tab: string }>();
  const { data: groupData, isLoading } = useGroup(groupId);
  const navigate = useNavigate();

  if (isLoading) {
    return <CenteredLoading />;
  }

  if (!groupData) {
    return <NotFound title={t('group_not_found_description')} buttonLabel={t('go_back_label')} />;
  }

  return (
    <Paper shadow={'sm'} radius={'md'} p={'md'} withBorder>
      <Tabs
        value={tab}
        onChange={(currentTab) => currentTab && navigate(`/settings/${groupId}/${currentTab}`)}
        defaultValue={GroupSettingsTabs.General}
      >
        <Tabs.List>
          <Tabs.Tab
            value={GroupSettingsTabs.General}
            leftSection={<BsClipboard size={DEFAULT_ICON_SIZE} />}
            data-testid={'group-settings-general-tab'}
          >
            {t('group_general_tab')}
          </Tabs.Tab>
          {!showUsaFeature && (
            <>
              <Tabs.Tab
                value={GroupSettingsTabs.Modules}
                leftSection={<BsFolder size={DEFAULT_ICON_SIZE} />}
                data-testid={'group-settings-modules-tab'}
              >
                {t('group_modules_title')}
              </Tabs.Tab>
            </>
          )}
          {showWorklistFeature && (
            <>
              <Tabs.Tab
                value={GroupSettingsTabs.Rules}
                leftSection={<BsDisplay size={DEFAULT_ICON_SIZE} />}
                data-testid={'group-settings-rules-tab'}
              >
                {t('monitoring_rules_title')}
              </Tabs.Tab>
            </>
          )}
        </Tabs.List>

        <Tabs.Panel value={GroupSettingsTabs.General} pt={'xs'}>
          {tab === GroupSettingsTabs.General && <GroupSettingsGeneral />}
        </Tabs.Panel>

        {!showUsaFeature && (
          <>
            <Tabs.Panel value={GroupSettingsTabs.Modules} pt={'xs'}>
              {tab === GroupSettingsTabs.Modules && <GroupSettingsModules />}
            </Tabs.Panel>
          </>
        )}
        {showWorklistFeature && (
          <>
            <Tabs.Panel value={GroupSettingsTabs.Rules} pt={'xs'}>
              {tab === GroupSettingsTabs.Rules && <GroupSettingsRules />}
            </Tabs.Panel>
          </>
        )}
      </Tabs>
    </Paper>
  );
};
